﻿import React from 'react';
import { Rocket, Github, Twitter, Mail, ExternalLink } from 'lucide-react';

interface FooterProps {
  t: any;
}

export function Footer({ t }: FooterProps) {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative mt-32 border-t border-white/10 bg-black/20 backdrop-blur-xl">
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div className="space-y-6 col-span-1 md:col-span-1">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full mars-gradient flex items-center justify-center">
                <Rocket className="text-white" size={16} />
              </div>
              <span className="text-xl font-bold tracking-tighter glow-text">小枫</span>
            </div>
            <p className="text-sm text-white/40 leading-relaxed">
              {t.footerDesc || '构建全息空间计算和独立 AI 人格的未来。'}
            </p>
            <div className="flex gap-4">
              <SocialLink icon={<Twitter size={18} />} href="#" />
              <SocialLink icon={<Github size={18} />} href="#" />
              <SocialLink icon={<Mail size={18} />} href="#" />
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-6">
            <h4 className="font-bold text-sm uppercase tracking-widest text-white/60">{t.links || '链接'}</h4>
            <ul className="space-y-3">
              <FooterLink label={t.docs || '文档'} href="#" />
              <FooterLink label={t.ecosystem || '生态系统'} href="#" />
              <FooterLink label={t.marketplace || '市场'} href="#" />
            </ul>
          </div>

          {/* Resources */}
          <div className="space-y-6">
            <h4 className="font-bold text-sm uppercase tracking-widest text-white/60">{t.resources || '资源'}</h4>
            <ul className="space-y-3">
              <FooterLink label={t.privacy || '隐私政策'} href="#" />
              <FooterLink label={t.terms || '服务条款'} href="#" />
              <FooterLink label={t.security || '安全'} href="#" />
            </ul>
          </div>

          {/* Newsletter */}
          <div className="space-y-6">
            <h4 className="font-bold text-sm uppercase tracking-widest text-white/60">{t.newsletter || '资讯订阅'}</h4>
            <p className="text-xs text-white/40">{t.newsletterDesc || '获取最新神经合成突破资讯。'}</p>
            <div className="flex gap-2">
              <input 
                type="email" 
                placeholder={t.emailPlaceholder || '邮箱地址'} 
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-celestial-saturn"
              />
              <button className="p-2 bg-celestial-saturn text-black rounded-xl hover:scale-105 transition-transform">
                <ExternalLink size={18} />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="space-y-1">
            <p className="text-xs text-white/45">
              © {currentYear} 小枫 Neural Systems. {t.allRightsReserved}
            </p>
            <p className="text-[12px] text-white/35 uppercase tracking-[0.2em] font-mono">
              {t.ethicalProtocol}
            </p>
          </div>
          <div className="flex gap-6 text-xs text-white/45">
            <span>v3.0.0-stable</span>
            <span className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              {t.systemsOnline}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ label, href }: { label: string; href: string }) {
  return (
    <li>
      <a href={href} className="text-sm text-white/40 hover:text-celestial-saturn transition-colors">
        {label}
      </a>
    </li>
  );
}

function SocialLink({ icon, href }: { icon: React.ReactNode; href: string }) {
  return (
    <a 
      href={href} 
      className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-white/40 hover:bg-celestial-saturn/10 hover:text-celestial-saturn transition-all"
    >
      {icon}
    </a>
  );
}
